import React from 'react'
import { compose, lifecycle, withHandlers, withProps, withState } from 'recompose'
import { connect } from 'react-redux'
import isEqual from 'lodash/isEqual'
import withType from 'components/common/hoc/withType'
import BOTPromotionBannerAndModal from './BOTPromotionBannerAndModal'
import CountdownTimer from 'components/BOTPromotionBannerAndModal/CountdownTimer'
import { dateToSeconds } from './CountdownTimer/helpers'
import { showPromotionModal, markPromotionModalShown } from './CustomPromotionModal/actions'
import { getPageLocation } from 'selectors'
import { buildComponents } from './helpers'
import { closeHeaderSystemAlertState, openHeaderSystemAlertState } from 'components/Header/HeaderSystemAlert/actions'
import { dispatchPageHeaderChanged } from 'helpers/custom-events'

import { getPromotionModalShownData } from './CustomPromotionModal/selectors'

const mapStateToProps = state => ({
  currentUrl: getPageLocation(state),
  promotionModalShown: getPromotionModalShownData(state)
})

const withBannerVisible = withState('isBannerVisible', 'setBannerVisible', false)
const withModalVisible = withState('isModalVisible', 'setModalVisible', false)

const withVisibilityHandler = withHandlers({
  handleVisibility: ({ setBannerVisible, setModalVisible, promotionModalShown, currentUrl, timerConfig }) => () => {
    const {
      startDate,
      endDate,
      banner: {
        pages: bannerPages = []
      } = {},
      modal: {
        pages: modalPages = []
      } = {}} = JSON.parse(timerConfig)

    const startToShowBannerDate = dateToSeconds(startDate)
    const endToShowBannerDate = dateToSeconds(endDate)
    const today = new Date().getTime()

    const startToShowBanner = (today - startToShowBannerDate) >= 0
    const endToShowBanner = (today - endToShowBannerDate) >= 0
    const isTimeActive = startToShowBanner && !endToShowBanner

    const { pathname } = currentUrl
    const path = pathname.toLowerCase()

    const isBannerVisible = isTimeActive && !(bannerPages.find(item => path.startsWith(item.toLowerCase())))
    const isModalVisible = isTimeActive && !(modalPages.find(item => path.startsWith(item.toLowerCase())))

    const isModalHiddenSessionStorage = window.sessionStorage.getItem('hidePromoModal')

    setBannerVisible(isBannerVisible)
    setModalVisible(isModalVisible && !promotionModalShown)

    if (isModalHiddenSessionStorage) {
      setModalVisible(!isModalHiddenSessionStorage)
    }
  }
})

const withBannerAndModalProps = withProps(({ bannerContent = '', timerConfig, ...props }) => {
  const {
    endDate,
    showOnlyBanner = false,
    banner: {
      timerLabels = {},
      hideSeconds
    } = {}
  } = JSON.parse(timerConfig)

  const countdownTimer = () =>
    <CountdownTimer endDate={ endDate } timerLabels={ timerLabels } hideSeconds={ hideSeconds } />

  const componentParts = buildComponents(bannerContent)

  return {
    components: {
      BeforeComponent: componentParts[0],
      TimerComponent: componentParts.length > 1 ? countdownTimer() : null,
      AfterComponent: componentParts.length > 1 ? componentParts[1] : null
    },
    timerConfig,
    showOnlyBanner,
    ...props
  }
})

const withModalLifecycle = lifecycle({
  componentDidMount() {
    this.props.handleVisibility()
  },

  componentDidUpdate({
    currentUrl: prevCurrentUrl,
    isBannerVisible: prevIsBannerVisible,
    isModalVisible: prevIsModalVisible }) {
    const {
      currentUrl,
      isModalVisible,
      isBannerVisible,
      handleVisibility,
      modalContent,
      timerConfig,
      showPromotionModal,
      closeHeaderSystemAlertState,
      openHeaderSystemAlertState,
      markPromotionModalShown,
      showOnlyBanner,
      promotionResultList,
      regionName,
      noPromotionsMessage = ''
    } = this.props

    if (!isEqual(currentUrl.pathname, prevCurrentUrl.pathname)) {
      handleVisibility()
    }

    if (!isEqual(isModalVisible, prevIsModalVisible) && Boolean(isModalVisible) && !showOnlyBanner) {
      setTimeout(() => {
        showPromotionModal({
          modalContent,
          timerConfig,
          promotionResultList,
          regionName,
          noPromotionsMessage
        })
        markPromotionModalShown()
      }, 2000)
    }

    if (!isEqual(isBannerVisible, prevIsBannerVisible)) {
      if (isBannerVisible) {
        closeHeaderSystemAlertState()
      } else {
        openHeaderSystemAlertState()
      }

      dispatchPageHeaderChanged()
    }
  }
})

export default compose(
  withType({ type: 'BOTPromotionBannerAndModal' }),
  connect(mapStateToProps, {
    showPromotionModal,
    closeHeaderSystemAlertState,
    openHeaderSystemAlertState,
    markPromotionModalShown
  }),
  withBannerVisible,
  withModalVisible,
  withVisibilityHandler,
  withBannerAndModalProps,
  withModalLifecycle
)(BOTPromotionBannerAndModal)
